import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { CardContent, Grid, Typography, TextField, InputAdornment, Box, Stack, Chip, Paper, Skeleton } from '@mui/material';
import MainCard from 'ui-component/cards/MainCard';
import { useTheme, alpha } from '@mui/material/styles';

// project imports
import { useDispatch, useSelector } from 'store';
import { getStateMachinesList } from 'store/slices/stateMachines';
import { gridSpacing } from 'store/constant';

// Icons
import { IconAnalyze, IconSearch, IconCalendarEvent, IconSettings } from '@tabler/icons';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// ==============================|| PROCESS LIST ||============================== //

const StateMachinesCard = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const [search, setSearch] = useState('');
    const { stateMachinesData, loading } = useSelector((state) => state.stateMachines);

    useEffect(() => {
        dispatch(getStateMachinesList());
    }, [dispatch]);

    const filteredData = useMemo(() => {
        const data = Array.isArray(stateMachinesData) ? stateMachinesData : [];
        if (!search) return data;
        return data.filter((item: any) => item.id?.toLowerCase().includes(search.toLowerCase()));
    }, [stateMachinesData, search]);

    return (
        <MainCard
            title={
                <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="h2" sx={{ fontWeight: 900, letterSpacing: '-0.5px' }}>
                            State Machines
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search state machines..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconSearch size="1.1rem" stroke={1.5} />
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.black, 0.4) : 'common.white',
                                    borderRadius: '12px'
                                }
                            }}
                        />
                    </Grid>
                </Grid>
            }
        >
            <CardContent sx={{ p: 0 }}>
                {loading && (
                    <Grid container spacing={gridSpacing}>
                        {[1, 2, 3].map((key) => (
                            <Grid item xs={12} lg={4} sm={6} key={key}>
                                <Skeleton variant="rectangular" height={160} sx={{ borderRadius: '16px' }} />
                            </Grid>
                        ))}
                    </Grid>
                )}

                {!loading && (
                    <Grid container spacing={gridSpacing}>
                        {filteredData?.map((item: any) => (
                            <Grid item xs={12} lg={4} sm={6} key={item.id}>
                                <Paper
                                    component={Link}
                                    to={`/stateMachines/${item.id}`}
                                    sx={{
                                        textDecoration: 'none',
                                        display: 'block',
                                        p: 3,
                                        borderRadius: '20px',
                                        bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.05) : 'primary.light',
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                        transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        '&:hover': {
                                            transform: 'translateY(-5px)',
                                            boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                                            borderColor: theme.palette.primary.main,
                                            '& .icon-box': {
                                                bgcolor: theme.palette.primary.main,
                                                color: '#fff'
                                            },
                                            '& .arrow-icon': {
                                                transform: 'translateX(5px)',
                                                opacity: 1
                                            }
                                        }
                                    }}
                                >
                                    <Box
                                        className="icon-box"
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                                            color: theme.palette.primary.main,
                                            mb: 2,
                                            transition: 'all 0.3s ease'
                                        }}
                                    >
                                        <IconSettings size="1.5rem" stroke={1.5} />
                                    </Box>

                                    <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, color: theme.palette.text.primary }}>
                                        {item.id}
                                    </Typography>

                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ opacity: 0.7, mb: 2 }}>
                                        <IconCalendarEvent size="0.9rem" />
                                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                            Created: {new Date(item.creationDate).toLocaleDateString()}
                                        </Typography>
                                    </Stack>

                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Chip
                                            label={item.status || 'ACTIVE'}
                                            size="small"
                                            color="success"
                                            sx={{
                                                fontWeight: 800,
                                                fontSize: '0.65rem',
                                                height: '20px',
                                                bgcolor: alpha(theme.palette.success.main, 0.1),
                                                color: theme.palette.success.main,
                                                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                                            }}
                                        />
                                        <ArrowForwardIcon
                                            className="arrow-icon"
                                            sx={{
                                                opacity: 0.3,
                                                transition: 'all 0.3s ease',
                                                color: theme.palette.primary.main
                                            }}
                                        />
                                    </Stack>

                                    {/* Decorative subtle gradient */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: -20,
                                            right: -20,
                                            width: 100,
                                            height: 100,
                                            background: `radial-gradient(circle, ${alpha(
                                                theme.palette.primary.main,
                                                0.05
                                            )} 0%, transparent 70%)`,
                                            zIndex: 0
                                        }}
                                    />
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                )}

                {!loading && filteredData?.length === 0 && (
                    <Box sx={{ py: 10, textAlign: 'center' }}>
                        <IconAnalyze size="4rem" stroke={1} style={{ opacity: 0.2, marginBottom: '20px' }} />
                        <Typography variant="h4" color="textSecondary" sx={{ fontWeight: 600 }}>
                            No state machines found matching &quot;{search}&quot;
                        </Typography>
                    </Box>
                )}
            </CardContent>
        </MainCard>
    );
};

export default StateMachinesCard;
